import axios, { Axios } from 'axios'
import { createStore } from './util'
import { notify } from '@/utils'
import Bowser from 'bowser'
import { getAccessToken } from '@privy-io/react-auth'

interface AppStore {
  http: Axios
  mode: 'light' | 'dark'
  page: string | null
  ip: string | null
  browserName: string | null
  loading: boolean

  setLoading: (loading: boolean) => void
  setMode: (mode: 'light' | 'dark') => void
  setPage: (page: string) => void
  clearPage: () => void
  getIp: () => void
  getBrowserName: () => void
}

export const useApp = createStore<AppStore>(
  'app',
  (set) => {
    const appEnvironment = import.meta.env.APP_ENVIRONMENT

    const isDev = appEnvironment !== 'staging' && appEnvironment !== 'production'

    // default dawn-api url is http://localhost:4042
    const baseURL: string =
      process.env.VITE_API_URL || (isDev ? 'http://localhost:4042' : 'https://ext-api.dawn.money')
    const http = axios.create({
      baseURL,
      withCredentials: true,
    })

    http.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config

        // Do not retry auth requests, as this can cause an infinite loop if the token is rejected
        if (originalRequest.url?.includes('/auth')) {
          return Promise.reject(error)
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true
          try {
            const newAccessToken = await getAccessToken()

            if (!newAccessToken) {
              throw new Error('Failed to retrieve new access token from Privy.')
            }

            window.localStorage.setItem('privy-token', newAccessToken)

            // Dynamically import useUser to prevent circular dependencies
            const { useUser } = await import('./user')
            useUser.getState().setToken(newAccessToken)

            originalRequest.headers['x-privy-token'] = newAccessToken
            return http(originalRequest)
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError)
            notify.error('Session expired. Please log in again.')
            return Promise.reject(refreshError)
          }
        }

        return Promise.reject(error)
      },
    )

    // inject token into request headers if its present
    http.interceptors.request.use(
      async (config) => {
        // Get the token from localStorage
        const token = window.localStorage.getItem('privy-token')

        if (token) {
          config.headers['x-privy-token'] = token
        }

        return config
      },
      (error) => {
        return Promise.reject(error)
      },
    )

    // Initialize mode from localStorage or default to 'dark'
    const savedMode = (window.localStorage.getItem('theme') as 'light' | 'dark') || 'dark'
    
    // Apply the initial theme class
    if (savedMode === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }

    return {
      http,
      mode: savedMode,
      page: null,
      ip: null,
      browserName: null,
      loading: false,
      setLoading: (loading: boolean) => {
        set({ loading })
      },
      getIp: async () => {
        try {
          const response = await http.get<{ ip: string }>('https://api.ipify.org?format=json')
          console.log('ip: ' + response.data.ip)
          set({ ip: response.data.ip })
        } catch {
          notify.error('Failed get IP')
          set({ ip: null })
        }
      },
      getBrowserName: () => {
        const browser = Bowser.getParser(window.navigator.userAgent)
        console.log('browserName: ' + browser.getBrowserName())
        set({ browserName: browser.getBrowserName() })
      },
      setMode: (mode) => {
        // Update HTML class for Tailwind dark mode
        if (mode === 'dark') {
          document.documentElement.classList.add('dark')
        } else {
          document.documentElement.classList.remove('dark')
        }
        
        // Save to localStorage
        window.localStorage.setItem('theme', mode)
        
        // Update store
        set({ mode })
      },
      setPage: (page: string) => {
        set({ page })
      },
      clearPage: () => {
        set({ page: null })
      },
    }
  },
  false,
)
